import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Added-to-cart panel: opens after an add to cart and shows the product(s) added while it is open,
 * the cross-sell of the product that opened it and a link to the cart page.
 * A bottom sheet on phones, a right-hand drawer on wider screens (CSS in assets/custom.css).
 *
 * The content is the section `custom-cart-added`, fetched after every add, so prices, images and texts
 * come from Liquid in the market's currency and language. Products added from the panel's own cross-sell
 * also fire a cart add event: they join the added lines, and the panel stays open.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} container - Where the fetched section content goes.
 *
 * @extends {DialogComponent<Refs>}
 */
class CartAddedPanel extends DialogComponent {
  requiredRefs = ['dialog', 'container'];

  /**
   * Adds that open the panel: the theme's product forms (buy box, sticky bar, cross-sell in this panel)
   * and the theme's own fetch-based add buttons (collection cards and tabs, product page cross-sell).
   * Other cart updates (quantity changes, removals) share the event name and are ignored.
   */
  static #sources = new Set([
    'product-form-component',
    'collection-products',
    'custom-collection-tabs',
    'custom-extra-products',
  ]);

  /** Variant ids added while the panel is open @type {Set<string>} */
  #addedVariantIds = new Set();

  /** Product ids of adds that did not say which variant (batch adds) @type {Set<string>} */
  #addedProductIds = new Set();

  /** The product whose cross-sell is shown: the first add since the panel opened @type {string} */
  #openerProductId = '';

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {AbortController | null} */
  #historyController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleCloseClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleCloseClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.#fetchController?.abort();
    this.#historyController?.abort();
  }

  /**
   * @param {Event} event
   */
  #handleCartAdd = async (event) => {
    // The theme's CartAddEvent is a plain Event with a detail property; the other buttons send a CustomEvent
    const { sourceId, data } = /** @type {{ detail?: { sourceId?: string, data?: any } }} */ (event).detail ?? {};
    if (!CartAddedPanel.#sources.has(data?.source) || data?.didError) return;

    // Product forms pass the variant id as sourceId (batch adds pass the form id instead);
    // the fetch-based buttons pass it as data.variantId and leave out the product id
    const variantId = String(data.variantId ?? (/^\d+$/.test(String(sourceId)) ? sourceId : ''));
    const productId = String(data.productId ?? '');
    if (!variantId && !productId) return;

    if (!this.refs.dialog.open) {
      this.#addedVariantIds = new Set();
      this.#addedProductIds = new Set();
      this.#openerProductId = '';
    }
    if (variantId) {
      this.#addedVariantIds.add(variantId);
    } else {
      this.#addedProductIds.add(productId);
    }
    if (!this.#openerProductId) this.#openerProductId = productId;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(this.dataset.sectionUrl ?? '', { signal: this.#fetchController.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('.cart-added__content');
      if (!content) return;

      this.refs.container.replaceChildren(content);
      this.#showRelevantContent(variantId);
      this.showDialog();
    } catch (error) {
      // An aborted request was replaced by a newer one; anything else leaves the page as it is
      // (the cart icon count is updated by the theme either way)
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.error(error);
    }
  };

  /**
   * Shows the lines added while the panel is open and the opener product's cross-sell; the rest stays hidden.
   * @param {string} variantId - The variant just added, if known
   */
  #showRelevantContent(variantId) {
    const { container } = this.refs;
    const lines = /** @type {HTMLElement[]} */ ([...container.querySelectorAll('.cart-added__line')]);

    // The fetch-based buttons don't say which product: take it from the added variant's line
    if (!this.#openerProductId && variantId) {
      const line = lines.find((element) => element.dataset.variantId === variantId);
      this.#openerProductId = line?.dataset.productId ?? '';
    }

    for (const line of lines) {
      line.hidden = !(
        this.#addedVariantIds.has(line.dataset.variantId ?? '') ||
        this.#addedProductIds.has(line.dataset.productId ?? '')
      );
    }

    for (const group of container.querySelectorAll('.cart-added__recs[data-product-id]')) {
      if (!(group instanceof HTMLElement)) continue;
      group.hidden = group.dataset.productId !== this.#openerProductId;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleCloseClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-cart-added-close]')) {
      this.closeDialog();
    }
  };

  /* Phones: the back button closes the panel instead of leaving the page (same as the theme's cart drawer) */
  #handleOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartAddedOpen) {
      history.pushState({ cartAddedOpen: true }, '');
    }

    this.#historyController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyController.signal });
  };

  #handleClose = () => {
    this.#historyController?.abort();
    if (history.state?.cartAddedOpen) {
      history.back();
    }
  };

  #handlePopState = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    }
  };
}

if (!customElements.get('cart-added-panel')) {
  customElements.define('cart-added-panel', CartAddedPanel);
}
